import React, { useCallback, useMemo, useState } from 'react';
import delay from 'lodash/delay';
import head from 'lodash/head';
import size from 'lodash/size';
import { useSelector } from 'react-redux';
import { CartEmpty } from '@yme/assets/icons';
import { BottomSheet, Header } from '@yme/atoms/bottom-sheet';
import { Button, CONTAINED_VARIANT, NO_ICON, SIZE_44 } from '@yme/atoms/button';
import { FlexibleCol } from '@yme/atoms/grid';
import { InfiniteScroll } from '@yme/atoms/infinite-scroll';
import { InfoBlockEmptyResults } from '@yme/atoms/info-block-empty-results';
import { Pills } from '@yme/atoms/pills';
import { ICON_SIZE, SVGIcon } from '@yme/atoms/svg-icon';
import { NOT_FOUND_URL } from '@yme/common/constants';
import { MobileFiltersButton, SortingFilter } from '@yme/components/SearchPage/components';
import { useFacetsFilter } from '@yme/components/SearchPage/hooks';
import { GridContainerStyled, GridRowStyled } from '@yme/components/SearchPage/styled';
import { useSimpleIsSticky } from '@yme/hooks/index';
import { SearchProductCard } from '@yme/molecules/cards';
import { CheckboxesDropdown, FilterDropdown, FiltersListSimpleResetStyled, } from '@yme/molecules/filter-dropdown';
import { getIsDriverLicenseEnabled, getIsInAuthor, getLanguageRootPath, getRootLang, } from '@yme/selectors/common';
import { useTranslation } from '@yme/services/i18n';
import { FlexWrapper } from '@yme/tokens/flexTokens';
import { INDENT } from '@yme/tokens/sizes';
import { RegularTextLevel2, RegularTextLevel3 } from '@yme/tokens/typography';
import { checkNoImageUrl, getSearchParams } from '@yme/utils/bloomreach';
import { getPDPLinkForBloomreachAccessory } from '@yme/utils/bloomreach/helpers';
import { getLatestModelTitle } from '@yme/utils/product';
import { OFFSET_FOR_STICKY, StickyFilter } from '../../StickyFilter';
import { getCountAppliedFilters, getCountAppliedValues, getPillsOptions, } from '../AccessoryApparelGrid/helpers';
import { MiniButtonStyled } from '../AccessoryApparelGrid/styled';
import { INFINITE_SCROLL_OFFSET_DESKTOP, INFINITE_SCROLL_OFFSET_MOBILE, } from '../ContentGrid/constants';
import { ModelsGridSkeleton } from './ModelsGridSkeleton';
import { getPricesValues, getProductCardImage, getVariantWithAvailableLowestPrise, } from './helpers';
export function ModelsGrid({ isRightToLeft, isInfiniteDesktopView, isMobileView, isLoaded, productItems, modelsFacets, numFound, query, category, getProductData, }) {
    const { unit_type } = getSearchParams();
    const [isFiltersBottomSheetOpened, setIsFiltersBottomSheetOpened] = useState(false);
    const isDriverLicenseEnabled = useSelector(getIsDriverLicenseEnabled);
    const languageRootPath = useSelector(getLanguageRootPath);
    const isInAuthor = useSelector(getIsInAuthor);
    const language = useSelector(getRootLang);
    const sizeProductItems = size(productItems);
    const isAllItemsLoaded = sizeProductItems === numFound;
    const { t } = useTranslation();
    const { sort } = getSearchParams();
    const { ref, isSticky } = useSimpleIsSticky(isInfiniteDesktopView ? OFFSET_FOR_STICKY : 0);
    const scrollToTop = useCallback(() => {
        if (isSticky) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        }
    }, [isSticky]);
    const { isSomeFacetsSelected, buildedFacets, selectedFacets, handleSelectOption, handleClearFilters, } = useFacetsFilter({
        initiaLFacets: modelsFacets,
        query,
        category,
        sort,
        callback: scrollToTop,
    });
    const countAppliedFilters = getCountAppliedFilters(buildedFacets);
    const filtersLabel = countAppliedFilters
        ? `${t('filters_filters_label')} (${countAppliedFilters})`
        : t('filters_filters_label');
    const handleOpenMobileFilters = useCallback(() => {
        setIsFiltersBottomSheetOpened(true);
    }, []);
    const handleCloseMobileFilters = useCallback(() => {
        delay(() => setIsFiltersBottomSheetOpened(false), 100);
    }, []);
    const fetchMoreProducts = useCallback(() => {
        if (!isAllItemsLoaded) {
            getProductData({
                query,
                primaryType: category,
                start: sizeProductItems,
                hasSpinner: true,
                sortingOrder: sort,
                facets: {
                    unit_type,
                },
            });
        }
    }, [isAllItemsLoaded, getProductData, query, category, sizeProductItems, sort, unit_type]);
    const modelsList = useMemo(() => productItems.map(({ pid, title, thumb_image, url, sku, driver_licence, is_electric, is_latest_model, is_ymt, is_new, product_year, segment_price_extra_information_tooltip_text, currency_code, variants = [], }) => {
        const { variantWithLowestPrice, isLowestPriseExist } = getVariantWithAvailableLowestPrise(variants);
        const { priceValue, discountedValue } = getPricesValues(language, currency_code, variantWithLowestPrice);
        const productTitle = getLatestModelTitle({
            isLatestModel: Boolean(is_latest_model),
            name: title,
            productYear: String(product_year),
        });
        const cardUrl = getPDPLinkForBloomreachAccessory({
            path: url,
            isInAuthor,
            sku,
        });
        return (React.createElement(FlexibleCol, { smMargin: `0 0 ${INDENT.m} 0`, sm: 6, md: 4, lg: 4, key: pid },
            React.createElement(SearchProductCard, { isLabelFromVisible: isLowestPriseExist, isRightToLeft: isRightToLeft, isElectricVehicle: Boolean(is_electric), isYAMTIconShowed: Boolean(is_ymt), isNewLabelShowed: Boolean(is_new), url: cardUrl || `${languageRootPath}/${NOT_FOUND_URL}`, image: getProductCardImage(checkNoImageUrl(thumb_image)), title: productTitle, driverLicense: isDriverLicenseEnabled ? driver_licence : undefined, price: priceValue, discountedValue: discountedValue, extraInformationTooltipText: segment_price_extra_information_tooltip_text })));
    }), [isDriverLicenseEnabled, isInAuthor, isRightToLeft, language, languageRootPath, productItems]);
    const Filters = (React.createElement(FilterDropdown, { isRightToLeft: isRightToLeft, titleLabel: t('productsGrid_modelTypeFilter_label'), selectedPlaceholderLabel: t('filters_optionsSelected_placeholder'), emptyPlaceholderLabel: t('filters_selectOptions_placeholder'), options: head(buildedFacets)?.values ?? [], isListItemView: !isInfiniteDesktopView, countAppliedValues: getCountAppliedValues(head(buildedFacets)?.values ?? []) },
        React.createElement(CheckboxesDropdown, { id: "Selected models", labels: {
                filters_selectAll_label: t('filters_selectAll_label'),
                filters_clearAll_label: t('filters_clearAll_label'),
                common_confirm_button: t('common_confirm_button'),
            }, onSelect: handleSelectOption(head(buildedFacets)?.name ?? ''), isRightToLeft: isRightToLeft, hasAdditionalControl: isInfiniteDesktopView, isCloseOnSelect: isInfiniteDesktopView && isSticky, isCounterAlwaysVisible: true })));
    const FiltersAndSorting = (React.createElement(React.Fragment, null,
        React.createElement(FlexibleCol, { sm: 4, md: 6, lg: 6 }, isInfiniteDesktopView ? (Filters) : (React.createElement(MobileFiltersButton, { onClick: handleOpenMobileFilters }))),
        React.createElement(FlexibleCol, { smMargin: isSticky ? undefined : `0 0 ${INDENT.sm} 0`, lgMargin: isSticky ? `0 0 ${INDENT.sm} 0` : undefined, sm: 8, md: 6, lg: 6 },
            React.createElement(SortingFilter, { isRightToLeft: isRightToLeft, isInfiniteDesktopView: isInfiniteDesktopView, category: category, callback: scrollToTop }))));
    const multiplePills = useMemo(() => getPillsOptions(selectedFacets, t, handleSelectOption), [handleSelectOption, selectedFacets, t]);
    if (!isLoaded) {
        return (React.createElement(GridContainerStyled, null,
            React.createElement(ModelsGridSkeleton, null)));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(StickyFilter, { isSticky: isSticky, isRightToLeft: isRightToLeft }, FiltersAndSorting),
        React.createElement(InfiniteScroll, { onReachEnd: fetchMoreProducts, offset: isMobileView ? INFINITE_SCROLL_OFFSET_MOBILE : INFINITE_SCROLL_OFFSET_DESKTOP },
            React.createElement(GridContainerStyled, null,
                React.createElement(GridRowStyled, { mt: "-16px", mdMt: "-16px", lgMt: "0px", ref: ref }, FiltersAndSorting),
                isSomeFacetsSelected && (React.createElement(GridRowStyled, { mb: INDENT.m, lgMb: INDENT.l },
                    React.createElement(FlexibleCol, { display: "flex", alignItems: "flex-start", justifyContent: isInfiniteDesktopView ? 'space-between' : 'flex-end' },
                        isInfiniteDesktopView && (React.createElement(FlexWrapper, { flexShrink: "1", mr: "24px" },
                            React.createElement(Pills, { multiplePills: multiplePills, isRightToLeft: isRightToLeft }))),
                        React.createElement(FlexWrapper, { flexShrink: "0" },
                            React.createElement(MiniButtonStyled, { onClick: handleClearFilters, isPaddingsReset: true, isUnderlined: true }, t('filters_clearFilters_label')))))),
                sizeProductItems > 0 ? (React.createElement(GridRowStyled, null, modelsList)) : (React.createElement(InfoBlockEmptyResults, { customIcon: React.createElement(SVGIcon, { type: CartEmpty, size: isMobileView ? ICON_SIZE.xxl : ICON_SIZE.xxxxxl }), verticalIndent: "48px", verticalIndentTablet: "72px", verticalIndentDesktop: "128px", titleEmptyResults: t('productsGrid_noProductsFound_label'), descriptionEmptyResults: t('productsGrid_couldNotFoundProducts_hint'), isFullWidth: true })))),
        React.createElement(BottomSheet, { initialFocusRef: false, open: isFiltersBottomSheetOpened, header: React.createElement(Header, { headerLeft: React.createElement(RegularTextLevel2, { weight: 500 }, filtersLabel), headerRight: isSomeFacetsSelected ? (React.createElement(RegularTextLevel3, { as: "span", onClick: handleClearFilters }, t('filters_clearFilters_label'))) : undefined }), footer: React.createElement(Button, { size: SIZE_44, hasIconButton: NO_ICON, buttonVariant: CONTAINED_VARIANT, label: isSomeFacetsSelected
                    ? `${t('common_showResults_button')} ${`(${numFound})`}`
                    : t('common_showResults_button'), onClick: handleCloseMobileFilters }), onDismiss: handleCloseMobileFilters },
            React.createElement(FiltersListSimpleResetStyled, null, Filters))));
}
